import React from 'react';
import CustomTextField from 'components/Fileds/CustomTextFiled';
import CustomSelect from 'components/Fileds/CustomSelect';
import CustomTypography from 'components/Fileds/CustomTypography';
import * as Yup from 'yup';
import GenericDialog from '../GenericDialog/GenericDialog';
import { useTheme } from '@mui/material';
import CustomTextArea from 'components/Fileds/CustomTextArea';

const WastageSchema = Yup.object().shape({
  inventory_item_id: Yup.number().required('Item is required'),
  qty: Yup.number()
    .typeError('Quantity must be a number')
    .positive('Quantity must be a positive number')
    .required('Quantity is required'),
  unit_type: Yup.string().required('Unit Type is required'),
  reason: Yup.string().required('Reason is required'),
  notes: Yup.string().nullable()
});

const WEIGHT_UNITS = ['Kg', 'gram'];
const VOLUME_UNITS = ['Liter', 'Ml'];
const DISCRETE_UNITS = ['Piece', 'Dozen', 'Box'];
const ALL_UNITS = [...WEIGHT_UNITS, ...VOLUME_UNITS, ...DISCRETE_UNITS];

const getCompatibleUnits = (itemUnit) => {
  if (WEIGHT_UNITS.includes(itemUnit)) return WEIGHT_UNITS;
  if (VOLUME_UNITS.includes(itemUnit)) return VOLUME_UNITS;
  if (DISCRETE_UNITS.includes(itemUnit)) return DISCRETE_UNITS;
  return ALL_UNITS;
};

const RecordWastageModal = ({ open, onClose, onSubmit, loading, inventoryItems = [], initialData }) => {
  const theme = useTheme();

  const itemOptions = inventoryItems.map(item => ({
    value: item.id,
    label: `${item.name} (${item.unit_type || ''})`
  }));

  const reasonOptions = [
    { value: 'Spoilage', label: 'Spoilage' },
    { value: 'Expired', label: 'Expired' },
    { value: 'Kitchen waste', label: 'Kitchen waste' },
    { value: 'Damaged', label: 'Damaged' },
    { value: 'Other', label: 'Other' }
  ];

  return (
    <GenericDialog
      open={open}
      onClose={onClose}
      title={initialData ? "Edit Wastage" : "Record Wastage"}
      initialValues={{
        inventory_item_id: initialData?.inventory_item_id || '',
        qty: initialData?.qty || '',
        unit_type: initialData?.unit_type || (initialData?.inventory_item_id ? inventoryItems.find(item => String(item.id) === String(initialData.inventory_item_id))?.unit_type : ''),
        reason: initialData?.reason || 'Spoilage',
        notes: initialData?.notes || ''
      }}
      validationSchema={WastageSchema}
      onSubmit={onSubmit}
      submitLabel={initialData ? "Save" : "Record"}
      loading={loading}
      requiredFields={['inventory_item_id', 'qty', 'unit_type', 'reason']}
      showCancel={true}
      cancelLabel="Cancel"
    >
      {({ values, errors, touched, handleChange, handleBlur, setFieldValue }) => {
        const selectedItem = inventoryItems.find(item => String(item.id) === String(values.inventory_item_id));
        const availableUnits = selectedItem
          ? getCompatibleUnits(selectedItem.unit_type)
          : ALL_UNITS;

        const unitTypeOptions = availableUnits.map(u => ({
          value: u,
          label: u
        }));

        return (
          <>
            <CustomTypography
              label="Item"
              sx={{ fontSize: '14px', fontWeight: 600, color: theme.palette.typographyLabel.main }}
            />
            <CustomSelect
              name="inventory_item_id"
              value={values.inventory_item_id}
              onChange={(e) => {
                const itemId = e.target.value;
                setFieldValue('inventory_item_id', itemId);
                const selectedItem = inventoryItems.find(item => String(item.id) === String(itemId));
                if (selectedItem) {
                  setFieldValue('unit_type', selectedItem.unit_type);
                } else {
                  setFieldValue('unit_type', '');
                }
              }}
              onBlur={handleBlur}
              options={itemOptions}
              placeholder="Select item"
              error={touched.inventory_item_id && Boolean(errors.inventory_item_id)}
              helperText={touched.inventory_item_id && errors.inventory_item_id}
            />

            <CustomTypography
              label="Quantity"
              sx={{ fontSize: '14px', fontWeight: 600, color: theme.palette.typographyLabel.main, mt: 2 }}
            />
            <CustomTextField
              name="qty"
              inputProps={{ inputMode: 'decimal' }}
              value={values.qty}
              onChange={(e) => {
                const val = e.target.value.replace(/[^0-9.]/g, '').replace(/(\..*)\./g, '$1');
                setFieldValue('qty', val);
              }}
              onBlur={handleBlur}
              fullWidth
              placeholder="Enter Quantity"
              error={touched.qty && Boolean(errors.qty)}
              helperText={touched.qty && errors.qty}
            />

            <CustomTypography
              label="Unit Type"
              sx={{ fontSize: '14px', fontWeight: 600, color: theme.palette.typographyLabel.main, mt: 2 }}
            />
            <CustomSelect
              name="unit_type"
              value={values.unit_type}
              onChange={handleChange}
              onBlur={handleBlur}
              options={unitTypeOptions}
              placeholder="Select Unit Type"
              error={touched.unit_type && Boolean(errors.unit_type)}
              helperText={touched.unit_type && errors.unit_type}
              disabled={!values.inventory_item_id}
            />

            <CustomTypography
              label="Reason"
              sx={{ fontSize: '14px', fontWeight: 600, color: theme.palette.typographyLabel.main, mt: 2 }}
            />
            <CustomSelect
              name="reason"
              value={values.reason}
              onChange={handleChange}
              onBlur={handleBlur}
              options={reasonOptions}
              placeholder="Select Reason"
              error={touched.reason && Boolean(errors.reason)}
              helperText={touched.reason && errors.reason}
            />

            <CustomTypography
              label="Notes"
              sx={{ fontSize: '14px', fontWeight: 600, color: theme.palette.typographyLabel.main, mt: 2 }}
            />
            <CustomTextArea
              name="notes"
              value={values.notes}
              onChange={handleChange}
              onBlur={handleBlur}
              fullWidth
              multiline
              rows={1}
              placeholder="Optional notes..."
              error={touched.notes && Boolean(errors.notes)}
              helperText={touched.notes && errors.notes}
            />
          </>
        );
      }}
    </GenericDialog>
  );
};

export default RecordWastageModal;
